import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import backArrow from "../../../assets/icons/backArrow.svg";
import { CustomButton, CustomInput } from "../../../global/components";
import strings from "../../../global/constants/StringConstants";
import {
  handleErrorMessage,
  openSuccessNotification,
} from "../../../helpers/methods";
import history from "../../../utils/history";
import { theme } from "../../../utils/styles";
import productManagementStyles from "./ProductManagement.styles";
import {
  deleteBannerSection,
  getProductSections,
  saveProductSections,
} from "./ProductManagementService";

export const PRODUCT_MANAGEMENT_STORAGE_KEY = "productManagementItems";

export interface ProductSection {
  id: number;
  imageFile: File | null;
  imagePreview: string;
  link: string;
}

export interface StoredProductItem {
  index: number;
  link: string;
  image: string;
}

const DEFAULT_BANNER_COUNT = 4;

const createEmptySection = (id: number): ProductSection => ({
  id,
  imageFile: null,
  imagePreview: "",
  link: "",
});

/** Ensure at least DEFAULT_BANNER_COUNT slots; fill missing with empty sections by id. */
function ensureBannerSlots(
  normalized: ProductSection[],
  count: number,
): ProductSection[] {
  const slots = Array.from({ length: count }, (_, i) => i + 1);
  return slots.map(
    (id) => normalized.find((s) => s.id === id) ?? createEmptySection(id),
  );
}

function getStoredItems(): StoredProductItem[] {
  try {
    const raw = localStorage.getItem(PRODUCT_MANAGEMENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function setStoredItems(items: StoredProductItem[]) {
  localStorage.setItem(PRODUCT_MANAGEMENT_STORAGE_KEY, JSON.stringify(items));
}

function itemsToSections(items: StoredProductItem[]): ProductSection[] {
  if (items.length === 0) return [createEmptySection(1)];
  return items
    .sort((a, b) => a.index - b.index)
    .map((item) => ({
      id: item.index,
      imageFile: null,
      imagePreview: item.image,
      link: item.link,
    }));
}

function getNextIdFromStorage(): number {
  const items = getStoredItems();
  return items.length > 0 ? items.length + 1 : 2;
}

/** Normalize API item to ProductSection; id = backend index. */
function normalizeSection(item: {
  index?: number;
  id?: number;
  link?: string;
  image?: string;
}): ProductSection {
  const backendIndex = item.index ?? item.id ?? 1;
  return {
    id: backendIndex,
    imageFile: null,
    imagePreview: item.image ?? "",
    link: item.link ?? "",
  };
}

/** Snapshot of last-saved state per section id (link + image url from server). */
type SavedSnapshot = Record<number, { link: string; image: string }>;

const ProductManagement = () => {
  const classes = productManagementStyles;
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [sections, setSections] = useState<ProductSection[]>([]);
  const [nextId, setNextId] = useState<number>(1);
  const [loadingFromApi, setLoadingFromApi] = useState(true);
  const [savedSnapshot, setSavedSnapshot] = useState<SavedSnapshot>({});
  const [savedInBackend, setSavedInBackend] = useState<Set<number>>(new Set());

  useEffect(() => {
    getBanners();
  }, []);

  const getBanners = async () => {
    try {
      setLoadingFromApi(true);
      const response = await getProductSections();
      const list = Array.isArray(response) ? response : (response?.items ?? []);
      const normalized = list.map((item: any) => normalizeSection(item));
      setSavedInBackend(new Set(normalized.map((s: ProductSection) => s.id)));
      const displaySections =
        normalized.length >= DEFAULT_BANNER_COUNT
          ? normalized
          : ensureBannerSlots(normalized, DEFAULT_BANNER_COUNT);
      setSections(displaySections);
      const snapshot: SavedSnapshot = {};
      displaySections.forEach((s: ProductSection) => {
        snapshot[s.id] = {
          link: s.link?.trim() ?? "",
          image: s.imagePreview ?? "",
        };
      });
      setSavedSnapshot(snapshot);
      const maxId =
        displaySections.length > 0
          ? Math.max(...displaySections.map((s: ProductSection) => s.id))
          : 0;
      setNextId(maxId + 1);
      setLoadingFromApi(false);
    } catch (error: any) {
      setLoadingFromApi(false);
      handleErrorMessage(error);
    }
  };

  const addSection = useCallback(() => {
    setSections((prev) => {
      const nextBackendIndex =
        prev.length === 0 ? 1 : Math.max(...prev.map((s) => s.id)) + 1;
      setNextId(nextBackendIndex + 1);
      return [...prev, createEmptySection(nextBackendIndex)];
    });
  }, []);

  const updateSection = useCallback(
    (id: number, updates: Partial<Omit<ProductSection, "id">>) => {
      setSections((prev) =>
        prev.map((s) => (s.id === id ? { ...s, ...updates } : s)),
      );
    },
    [],
  );

  const handleFileChange = useCallback(
    (id: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !file.type.startsWith("image/")) return;
      const preview = URL.createObjectURL(file);
      updateSection(id, { imageFile: file, imagePreview: preview });
      e.target.value = "";
    },
    [updateSection],
  );

  const handleDrop = useCallback(
    (id: number, e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (!file || !file.type.startsWith("image/")) return;
      const preview = URL.createObjectURL(file);
      updateSection(id, { imageFile: file, imagePreview: preview });
    },
    [updateSection],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const removeImage = useCallback(
    (id: number, previewUrl: string) => {
      if (previewUrl && previewUrl.startsWith("blob:"))
        URL.revokeObjectURL(previewUrl);
      updateSection(id, { imageFile: null, imagePreview: "" });
    },
    [updateSection],
  );

  const [savingSectionId, setSavingSectionId] = useState<number | null>(null);

  const canSaveSection = useCallback((section: ProductSection) => {
    const linkTrimmed = section.link?.trim() ?? "";
    const hasImage =
      !!section.imageFile || (section.imagePreview?.trim() ?? "").length > 0;
    return linkTrimmed.length > 0 && hasImage;
  }, []);

  const hasUnsavedChanges = useCallback(
    (section: ProductSection): boolean => {
      const saved = savedSnapshot[section.id];
      if (!saved) return true;
      const linkTrimmed = section.link?.trim() ?? "";
      const linkChanged = linkTrimmed !== (saved.link?.trim() ?? "");
      const imageChanged =
        section.imageFile != null || section.imagePreview !== saved.image;
      return linkChanged || imageChanged;
    },
    [savedSnapshot],
  );

  const canEnableSaveButton = useCallback(
    (section: ProductSection): boolean => {
      if (!canSaveSection(section)) return false;
      return hasUnsavedChanges(section);
    },
    [canSaveSection, hasUnsavedChanges],
  );

  const handleSaveSection = useCallback(async (section: ProductSection) => {
    const linkTrimmed = section.link?.trim() ?? "";
    if (!linkTrimmed) return;
    if (!section.imageFile && !section.imagePreview) return;
    try {
      setSavingSectionId(section.id);
      const formData = new FormData();
      formData.append("index", String(section.id));
      formData.append("link", linkTrimmed);
      if (section.imageFile) {
        formData.append("file", section.imageFile);
      }
      await saveProductSections(formData);
      await getBanners();
      openSuccessNotification("Section saved successfully.");
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSavingSectionId(null);
    }
  }, []);

  const handleDeleteBanner = useCallback(
    async (id: number) => {
      if (!savedInBackend.has(id)) {
        setSections((prev) => {
          const filtered = prev.filter((s) => s.id !== id);
          if (filtered.length >= DEFAULT_BANNER_COUNT) return filtered;
          const maxId =
            filtered.length > 0 ? Math.max(...filtered.map((s) => s.id)) : 0;
          const needed = DEFAULT_BANNER_COUNT - filtered.length;
          const extras = Array.from({ length: needed }, (_, i) =>
            createEmptySection(maxId + i + 1),
          );
          return [...filtered, ...extras];
        });
        return;
      }
      try {
        await deleteBannerSection(id);
        openSuccessNotification("Banner deleted successfully.");
      } catch (error: any) {
        handleErrorMessage(error);
      } finally {
        getBanners();
      }
    },
    [savedInBackend, getBanners],
  );

  const getHeader = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: { sm: "center", xs: "baseline" },
        width: "100%",
      }}
      pb={3}
    >
      <Box
        display="flex"
        alignItems="center"
        sx={{ cursor: "pointer", width: "fit-content" }}
        onClick={() => history.goBack()}
      >
        {isMobile ? (
          <img src={backArrow} height="20px" alt="Back" />
        ) : (
          <img src={backArrow} height="25px" alt="Back" />
        )}
      </Box>
      <Typography sx={classes.italicTypo}>
        {t("productManagement") !== "productManagement"
          ? t("productManagement")
          : "Product Management"}
      </Typography>
      <Button
        startIcon={<AddIcon />}
        sx={classes.addSectionBtn}
        onClick={addSection}
      >
        {t("addSection") !== "addSection"
          ? t("addSection")
          : "Add another section"}
      </Button>
    </Box>
  );
  return (
    <Box sx={classes.mainContainer}>
      <Box sx={classes.mainBox}>
        <Box sx={classes.subBox}>
          <Box sx={classes.scrollStyle}>
            <Box sx={classes.bannersContent}>
              <Container
                maxWidth="xl"
                sx={{
                  px: 1,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                {getHeader()}
                <Grid container>
                  {sections.map((section, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      key={section.id}
                      sx={classes.bannerSlot}
                    >
                      <Box sx={classes.sectionHeader}>
                        <Typography
                          component="span"
                          sx={classes.bannerSlotTitle}
                        >
                          Banner {index + 1}
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<DeleteOutlineIcon />}
                          sx={classes.removeSectionBtn}
                          onClick={() => handleDeleteBanner(section.id)}
                        >
                          {t("remove") !== "remove" ? t("remove") : "Remove"}
                        </Button>
                      </Box>

                      <Box sx={classes.linkFieldCard}>
                        <Typography sx={classes.linkFieldLabel}>
                          {t("link") !== "link" ? t("link") : "Link"}
                        </Typography>
                        <CustomInput
                          placeHolder="https://..."
                          value={section.link}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateSection(section.id, {
                              link: e.target.value,
                            })
                          }
                          customClasses={classes.textField}
                          customNameFieldClasses={classes.nameFieldHidden}
                        />
                      </Box>

                      <Box sx={classes.dropzoneWrap}>
                        <input
                          id={`product-upload-${section.id}`}
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={(e) => handleFileChange(section.id, e)}
                        />
                        <Box
                          sx={{
                            ...classes.dropzone,
                            ...(section.imagePreview
                              ? classes.dropzoneWithPreview
                              : {}),
                          }}
                          onDrop={(e) => handleDrop(section.id, e)}
                          onDragOver={handleDragOver}
                          onClick={() => {
                            if (!section.imagePreview) {
                              document
                                .getElementById(`product-upload-${section.id}`)
                                ?.click();
                            }
                          }}
                        >
                          {section.imagePreview ? (
                            <Box
                              sx={classes.previewInside}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <img
                                src={section.imagePreview}
                                alt={`Preview ${index + 1}`}
                                style={classes.previewImage}
                              />
                              <Button
                                size="small"
                                sx={classes.removeImageBtn}
                                onClick={(e: React.MouseEvent) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  removeImage(section.id, section.imagePreview);
                                }}
                                aria-label="Remove image"
                              >
                                <CloseIcon fontSize="small" />
                              </Button>
                            </Box>
                          ) : (
                            <>
                              <CloudUploadIcon sx={classes.dropzoneIcon} />
                              <Typography sx={classes.dropzoneText}>
                                {t("dropImageOrClick") !== "dropImageOrClick"
                                  ? t("dropImageOrClick")
                                  : "Drop or click"}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>

                      <Box sx={classes.saveBtnWrap}>
                        <CustomButton
                          label={
                            savingSectionId === section.id
                              ? t("saving") !== "saving"
                                ? t("saving")
                                : "Saving..."
                              : t("save")
                          }
                          type={strings.PRIMARY}
                          onClick={() => handleSaveSection(section)}
                          disabled={
                            !canEnableSaveButton(section) ||
                            savingSectionId === section.id
                          }
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Container>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductManagement;
